import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Formik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import OtpCodeInput from "../../components/shared/OtpCodeInput";
import JalaliDatePicker from "../../components/shared/jalali-datepicker";

import DefaultApiService from "../../../infrastructure/api/default";
import CustomerRequestLocationService from "../../../infrastructure/api/customerRequestLocation";
import PartnerAssistedRegistrationApi from "../../../infrastructure/api/partnerAssistedRegistrationApi";
import { formatNumber } from "../../../infrastructure/utils/formatters";
import { formatLocalDateForApi } from "../../middleware/middleware";
import {
  getDerivedLoanFormValues,
  resolvePlantLoanPlan,
} from "../../../infrastructure/utils/plantLoanPlans";
import {
  assistedProfileValidationSchema,
  getAssistedRequestStepSchema,
} from "../SLN/shared/customerRequest/customerRequestValidationSchemas";
import {
  defaultAssistedRequestForm,
  formatApplicantGoalsLabel,
  parsePlantCapacityForApi,
  plantCapacityFormValue,
  resolvePlantCapacityOptions,
} from "./assistedRegistrationFormConstants";
import AssistedRegistrationRequestSteps from "./components/AssistedRegistrationRequestSteps";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import ValidationErrorsAlert from "./components/ValidationErrorsAlert";
import { formatValidationErrorList } from "./assistedRegistrationValidationUtils";
import {
  ASSISTED_REGISTRATION_ID_QUERY,
  buildAssistedRegistrationSearchParams,
  readAssistedRegistrationIdFromQuery,
  resolveAssistedRegistrationId,
} from "./assistedRegistrationRouteUtils";

const STEPS = [
  "شماره موبایل",
  "تأیید پیامک",
  "پروفایل متقاضی",
  "شرایط احداث",
  "مشخصات نیروگاه",
  "موقعیت و آدرس",
  "بارگذاری مدارک",
  "بررسی و ارسال",
];

const defaultProfile = {
  firstName: "",
  lastName: "",
  fatherName: "",
  birthDate: null,
  nationalCode: "",
  email: "",
  telephon: "",
  agreementKind: 1,
  companyId: 1,
};

const FORM_NARROW_MAX = 480;
const FORM_WIDE_MAX = 880;

const StepFormCard = ({ children, maxWidth = FORM_WIDE_MAX, title, description }) => (
  <Card
    variant="outlined"
    sx={{
      maxWidth,
      mx: "auto",
      width: "100%",
      borderRadius: 2,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {title ? (
        <Typography variant="h6" fontWeight={700} sx={{ mb: description ? 0.5 : 2 }}>
          {title}
        </Typography>
      ) : null}
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </CardContent>
  </Card>
);

const FormActions = ({ children }) => (
  <Box sx={{ mt: 2.5, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
    {children}
  </Box>
);

const ActionButton = ({ loading, children, disabled, ...props }) => (
  <Button disabled={disabled || loading} {...props}>
    {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
    {children}
  </Button>
);

const asFormText = (value) => (value == null ? "" : String(value));

const mapRequestToForm = (request = {}) => ({
  ...defaultAssistedRequestForm,
  ...request,
  powerPlantType: request.powerPlantType != null ? String(request.powerPlantType) : "",
  locationUsageType: request.locationUsageType != null ? String(request.locationUsageType) : "",
  plantCapacity: plantCapacityFormValue(request.plantCapacity),
  ownershipStatus: request.ownershipStatus != null ? String(request.ownershipStatus) : "",
  platformAcquaintanceSource:
    request.platformAcquaintanceSource != null ? String(request.platformAcquaintanceSource) : "",
  provinceId: request.provinceId ? String(request.provinceId) : "",
  cityId: request.cityId ? String(request.cityId) : "",
  installationType:
    request.installationType === 2 || request.installationType === "Apartment"
      ? "Apartment"
      : "Villa",
  unitCount: request.unitCount != null && request.unitCount !== "" ? String(request.unitCount) : "",
  xPoint: request.xPoint || "",
  yPoint: request.yPoint || "",
  locationInput: asFormText(request.locationInput),
  electricBoillUrl: asFormText(request.electricBoillUrl),
  plantInstallImage1Url: asFormText(request.plantInstallImage1Url),
  plantInstallImage2Url: asFormText(request.plantInstallImage2Url),
  nationalCardUrl: asFormText(request.nationalCardUrl),
  nationalCardBackUrl: asFormText(request.nationalCardBackUrl),
  boardConsentDocumentUrl: asFormText(request.boardConsentDocumentUrl),
  authorizedRepresentativeLetterUrl: asFormText(request.authorizedRepresentativeLetterUrl),
  agreementDocumentUrl: asFormText(request.agreementDocumentUrl),
  homeOwnerShipDocUrl: asFormText(request.homeOwnerShipDocUrl),
  note: asFormText(request.note),
  needsLoan: Boolean(request.needsLoan ?? request.NeedsLoan),
  loanGuaranteeChequeIntent:
    request.loanGuaranteeChequeIntent != null
      ? String(request.loanGuaranteeChequeIntent)
      : request.LoanGuaranteeChequeIntent != null
        ? String(request.LoanGuaranteeChequeIntent)
        : "",
  guarantorFirstName: asFormText(
    request.guarantorFirstName ?? request.GuarantorFirstName
  ),
  guarantorLastName: asFormText(
    request.guarantorLastName ?? request.GuarantorLastName
  ),
  guarantorNationalCode: asFormText(
    request.guarantorNationalCode ?? request.GuarantorNationalCode
  ),
  guarantorMobile: asFormText(request.guarantorMobile ?? request.GuarantorMobile),
});

const buildRequestPayload = (values) => {
  const installationType = values.installationType === "Apartment" ? 2 : 1;
  let note = values.note?.trim() || "";
  if (values.needsLoan && values.powerPlantType) {
    const loanPlan = resolvePlantLoanPlan(values.powerPlantType);
    if (loanPlan.available) {
      const derived = getDerivedLoanFormValues(values.powerPlantType);
      const loanLine = `[وام] طرح ${derived.loanPlanType} — سقف ${formatNumber(loanPlan.maxLoanAmount)} ریال، ${formatNumber(loanPlan.months)} قسط`;
      note = note ? `${note}\n${loanLine}` : loanLine;
    }
  }

  return {
    plantKind: Number(values.plantKind) || 1,
    companyId: Number(values.companyId) || 0,
    provinceId: Number(values.provinceId) || 0,
    cityId: Number(values.cityId) || 0,
    address: values.address?.trim() || "",
    postalCode: values.postalCode?.trim() || "",
    electricBoillNumber: values.electricBoillNumber?.trim() || "",
    electricBoillUrl: values.electricBoillUrl || "",
    plantInstallImage1Url: values.plantInstallImage1Url || "",
    plantInstallImage2Url: values.plantInstallImage2Url || "",
    nationalCardUrl: values.nationalCardUrl || "",
    nationalCardBackUrl: values.nationalCardBackUrl || "",
    homeOwnerShipDocUrl: values.homeOwnerShipDocUrl || "",
    plantCapacity: parsePlantCapacityForApi(values.plantCapacity),
    powerPlantType: values.powerPlantType ? Number(values.powerPlantType) : null,
    locationUsageType: values.locationUsageType ? Number(values.locationUsageType) : null,
    installableAreaSquareMeter: values.installableAreaSquareMeter
      ? Number(values.installableAreaSquareMeter)
      : null,
    ownershipStatus: values.ownershipStatus ? Number(values.ownershipStatus) : null,
    platformAcquaintanceSource: values.platformAcquaintanceSource
      ? Number(values.platformAcquaintanceSource)
      : null,
    applicantGoalFlags: Number(values.applicantGoalFlags) || 0,
    installationType,
    unitCount: values.unitCount ? Number(values.unitCount) : null,
    xPoint: Number(values.xPoint) || 0,
    yPoint: Number(values.yPoint) || 0,
    locationInput: values.locationInput?.trim() || "",
    note,
    needsLoan: Boolean(values.needsLoan),
    loanGuaranteeChequeIntent:
      values.needsLoan && values.loanGuaranteeChequeIntent
        ? Number(values.loanGuaranteeChequeIntent)
        : null,
    guarantorFirstName:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorFirstName || "").trim()
        : null,
    guarantorLastName:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorLastName || "").trim()
        : null,
    guarantorNationalCode:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorNationalCode || "").replace(/\D/g, "")
        : null,
    guarantorMobile:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorMobile || "").replace(/\D/g, "")
        : null,
    GuarantorFirstName:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorFirstName || "").trim()
        : null,
    GuarantorLastName:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorLastName || "").trim()
        : null,
    GuarantorNationalCode:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorNationalCode || "").replace(/\D/g, "")
        : null,
    GuarantorMobile:
      values.needsLoan && String(values.loanGuaranteeChequeIntent) === "2"
        ? String(values.guarantorMobile || "").replace(/\D/g, "")
        : null,
    boardConsentDocumentUrl: values.boardConsentDocumentUrl || "",
    authorizedRepresentativeLetterUrl: values.authorizedRepresentativeLetterUrl || "",
    agreementDocumentUrl: values.agreementDocumentUrl || "",
  };
};

const AssistedRegistrationPage = ({
  api = PartnerAssistedRegistrationApi,
  pageShell: PageShell = PartnerCommissionPageShell,
  title = "ثبت متقاضی جدید",
  description = "ثبت مشتری با تأیید موبایل، تکمیل پروفایل و ارسال درخواست نیروگاه — مشابه پنل متقاضی.",
  submitLockedMessage = "امکان ویرایش توسط نماینده وجود ندارد.",
  listBackPath = null,
} = {}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [registrationId, setRegistrationId] = useState(null);
  const [leadCode, setLeadCode] = useState("");
  const [locked, setLocked] = useState(false);
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(defaultProfile);
  const [requestForm, setRequestForm] = useState(defaultAssistedRequestForm);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [plantCapacityOptions, setPlantCapacityOptions] = useState([]);
  const [validationMessages, setValidationMessages] = useState([]);
  const [profileErrors, setProfileErrors] = useState({});
  const formikRef = useRef(null);
  // After start, OTP must be entered in this session before profile/request steps.
  const awaitingOtpRef = useRef(false);

  const baseUrl = window.globalConfig?.API_URL || "";

  const applyRegistrationDetail = useCallback((data, { forceStep } = {}) => {
    if (!data) return;

    if (data.mobile) setMobile(data.mobile);
    setLeadCode(data.leadCode || "");

    if (data.isLocked) {
      setLocked(true);
      setActiveStep(7);
      return;
    }

    if (data.profile) {
      setProfile({
        ...defaultProfile,
        firstName: data.profile.firstName || "",
        lastName: data.profile.lastName || "",
        fatherName: data.profile.fatherName || "",
        nationalCode: data.profile.nationalCode || "",
        email: data.profile.email || "",
        telephon: data.profile.telephon || "",
        birthDate: (() => {
          const birthRaw = data.profile.birthDate ?? data.profile.BirthDate;
          if (!birthRaw) return null;
          const d = new Date(birthRaw);
          return Number.isNaN(d.getTime()) ? null : d;
        })(),
      });
    }

    if (data.request) {
      setRequestForm(mapRequestToForm(data.request));
    }

    if (forceStep != null) {
      setActiveStep(forceStep);
      return;
    }

    const status = Number(data.status) || 0;
    // 1 = PendingMobileVerification → OTP
    if (status <= 1) {
      setActiveStep(1);
      return;
    }
    // 2..3 = mobile verified / profile draft → profile
    if (status <= 3) {
      setActiveStep(2);
      return;
    }
    // 4 = profile completed → conditions / request wizard
    if (status === 4) {
      setActiveStep(3);
      return;
    }
    // 5+ = request draft or later editable → documents/review area
    if (data.request) {
      setActiveStep(6);
      return;
    }
    setActiveStep(3);
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const { provinces: p, cities: c } =
        await CustomerRequestLocationService.loadCustomerRequestLocationLists();
      setProvinces(p || []);
      setCities(c || []);
    } catch {
      setProvinces([]);
      setCities([]);
    }
  }, []);

  const loadEnums = useCallback(async () => {
    try {
      const res = await DefaultApiService.getOneApi("SLN/Customer/Requests", 0);
      setPlantCapacityOptions(resolvePlantCapacityOptions(res));
    } catch {
      setPlantCapacityOptions(resolvePlantCapacityOptions(null));
    }
  }, []);

  const syncRegistrationQuery = useCallback(
    (id) => {
      if (!id) return;
      setSearchParams(buildAssistedRegistrationSearchParams(id));
    },
    [setSearchParams]
  );

  const clearRegistrationQuery = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(ASSISTED_REGISTRATION_ID_QUERY);
      next.delete("leadId");
      return next;
    });
  }, [setSearchParams]);

  const loadExistingRegistration = useCallback(
    async (id) => {
      // OTP just sent in this session — keep the OTP screen, no full-page reload flash.
      if (awaitingOtpRef.current) {
        setRegistrationId(id);
        setActiveStep(1);
        return;
      }

      setPageLoading(true);
      try {
        const res = await api.getDetail(id);
        if (!res.isSuccess) {
          clearRegistrationQuery();
          toast.error(res.message);
          return;
        }
        const data = res.data;
        const resolvedId = resolveAssistedRegistrationId(data) ?? id;
        setRegistrationId(resolvedId);
        syncRegistrationQuery(resolvedId);

        if (data.isLocked) {
          applyRegistrationDetail(data);
          const summaryRes = await api.getSummary(resolvedId);
          if (summaryRes.isSuccess) setSummary(summaryRes.data);
          return;
        }

        applyRegistrationDetail(data);
      } catch {
        clearRegistrationQuery();
        toast.error("بارگذاری پرونده ناموفق بود.");
      } finally {
        setPageLoading(false);
      }
    },
    [applyRegistrationDetail, clearRegistrationQuery, syncRegistrationQuery]
  );

  useEffect(() => {
    loadLocations();
    loadEnums();
    const fromUrl = readAssistedRegistrationIdFromQuery(searchParams);
    if (fromUrl) loadExistingRegistration(fromUrl);
  }, [loadLocations, loadEnums, loadExistingRegistration, searchParams]);

  useEffect(() => {
    setValidationMessages([]);
    setProfileErrors({});
  }, [activeStep]);

  const showValidationFeedback = (err) => {
    const messages = formatValidationErrorList(err);
    setValidationMessages(messages);
    if (messages.length > 0) {
      toast.error(messages[0]);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleParseLocation = useCallback(async (locationInput) => {
    try {
      const res = await api.parseLocation(locationInput);
      if (!res.isSuccess) {
        const err = new Error(res.message || "خطا در پردازش موقعیت.");
        err.parseFailed = true;
        throw err;
      }
      return res.data;
    } catch (err) {
      if (err?.parseFailed) throw err;
      const wrapped = new Error(err?.message || "خطا در پردازش موقعیت.");
      wrapped.parseFailed = true;
      throw wrapped;
    }
  }, []);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      const res = await api.start(mobile);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      const nextRegistrationId = resolveAssistedRegistrationId(res.data);
      if (!nextRegistrationId) {
        toast.error("شناسه پرونده از سرور دریافت نشد.");
        return;
      }
      awaitingOtpRef.current = true;
      setOtp("");
      setLocked(false);
      setSummary(null);
      setProfile(defaultProfile);
      setRequestForm(defaultAssistedRequestForm);
      setProfileErrors({});
      setValidationMessages([]);
      setRegistrationId(nextRegistrationId);
      setLeadCode(res.data.leadCode);
      setActiveStep(1);
      syncRegistrationQuery(nextRegistrationId);
      toast.success(res.data.message || "کد ارسال شد.");
    } catch {
      toast.error("ارسال کد ناموفق بود.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    setActionLoading(true);
    try {
      const res = await api.verifyOtp(mobile, otp);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      const nextRegistrationId = resolveAssistedRegistrationId(res.data);
      if (!nextRegistrationId) {
        toast.error("شناسه پرونده از سرور دریافت نشد.");
        return;
      }
      awaitingOtpRef.current = false;
      setRegistrationId(nextRegistrationId);
      setLeadCode(res.data.leadCode || leadCode);
      syncRegistrationQuery(nextRegistrationId);

      // Load existing profile/request only after OTP is confirmed in this session.
      const detailRes = await api.getDetail(nextRegistrationId);
      if (detailRes.isSuccess) {
        applyRegistrationDetail(detailRes.data);
      } else {
        setActiveStep(2);
      }
      toast.success("موبایل مشتری تأیید شد.");
    } catch {
      toast.error("تأیید کد ناموفق بود.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackToMobile = () => {
    awaitingOtpRef.current = false;
    setOtp("");
    setActiveStep(0);
  };

  const handleSaveProfile = async () => {
    if (!registrationId) return;
    try {
      await assistedProfileValidationSchema.validate(profile, { abortEarly: false });
      setProfileErrors({});
      setValidationMessages([]);
    } catch (err) {
      const messages = formatValidationErrorList(err);
      setValidationMessages(messages);
      const nextProfileErrors = {};
      err.inner?.forEach((e) => {
        if (e.path) nextProfileErrors[e.path] = e.message;
      });
      setProfileErrors(nextProfileErrors);
      if (messages[0]) toast.error(messages[0]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        ...profile,
        companyId: Number(profile.companyId) || 1,
        fatherName: (profile.fatherName || "").trim(),
        birthDate: profile.birthDate ? formatLocalDateForApi(profile.birthDate) : null,
      };
      const res = await api.saveProfile(registrationId, payload);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      setActiveStep(3);
      toast.success("پروفایل ذخیره شد.");
    } catch {
      toast.error("ذخیره پروفایل ناموفق بود.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestStepNext = async (values, { setErrors, setTouched, setSubmitting }) => {
    const requestStep = activeStep - 3;
    const schema = getAssistedRequestStepSchema(requestStep);

    let formValue = { ...values };
    if (requestStep === 2) {
      formValue = {
        ...formValue,
        xPoint: formValue.xPoint ? Number(formValue.xPoint) : formValue.xPoint,
        yPoint: formValue.yPoint ? Number(formValue.yPoint) : formValue.yPoint,
      };
    }

    try {
      await schema.validate(formValue, { abortEarly: false });
      setValidationMessages([]);
    } catch (validationErrors) {
      const nextErrors = {};
      const nextTouched = {};
      validationErrors.inner?.forEach((error) => {
        nextErrors[error.path] = error.message;
        nextTouched[error.path] = true;
      });
      setErrors(nextErrors);
      setTouched(nextTouched);
      setSubmitting(false);
      showValidationFeedback(validationErrors);
      return;
    }

    setRequestForm(formValue);

    if (requestStep === 3) {
      setActionLoading(true);
      try {
        const payload = buildRequestPayload(formValue);
        if (!payload.plantCapacity) {
          toast.error("ظرفیت نیروگاه نامعتبر است.");
          setSubmitting(false);
          return;
        }
        const res = await api.saveRequest(registrationId, payload);
        if (!res.isSuccess) {
          toast.error(res.message);
          setSubmitting(false);
          return;
        }
        setActiveStep(7);
        toast.success("درخواست ذخیره شد.");
      } catch {
        toast.error("ذخیره درخواست ناموفق بود.");
      } finally {
        setActionLoading(false);
        setSubmitting(false);
      }
      return;
    }

    if (requestStep === 4) {
      await handleSubmit();
      setSubmitting(false);
      return;
    }

    setActiveStep((s) => s + 1);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!registrationId) return;
    setActionLoading(true);
    try {
      const res = await api.submit(registrationId);
      if (!res.isSuccess) {
        toast.error(res.message);
        return;
      }
      setLocked(true);
      setSummary(res.data);
      const trackingNo = res.data?.referenceId ?? res.data?.ReferenceId;
      toast.success(
        trackingNo
          ? `درخواست ثبت شد. شماره پیگیری: ${trackingNo}`
          : "پرونده برای بررسی سولارنت ارسال شد."
      );
      if (listBackPath && registrationId) {
        navigate(
          `${listBackPath}?${ASSISTED_REGISTRATION_ID_QUERY}=${registrationId}`
        );
      }
    } catch {
      toast.error("ارسال ناموفق بود.");
    } finally {
      setActionLoading(false);
    }
  };

  const requestStep = activeStep - 3;
  const profileSummary = { ...profile, mobile };

  return (
    <PageShell
      title={title}
      description={description}
      loading={pageLoading}
    >
      {listBackPath ? (
        <Box sx={{ maxWidth: FORM_WIDE_MAX, mx: "auto", width: "100%", mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(listBackPath)}
            variant="text"
          >
            بازگشت به لیست
          </Button>
        </Box>
      ) : null}
      <Box sx={{ maxWidth: FORM_WIDE_MAX, mx: "auto", width: "100%" }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {leadCode ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
            کد لید: {leadCode}
          </Typography>
        ) : null}
      </Box>

      {locked && summary ? (
        <StepFormCard maxWidth={FORM_WIDE_MAX} title="ثبت با موفقیت انجام شد">
          <Alert severity="success" sx={{ mb: 2 }}>
            {submitLockedMessage}
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            شماره پیگیری را به متقاضی اعلام کنید. پیامک ثبت درخواست نیز برای متقاضی ارسال شده
            است.
          </Alert>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.main",
              bgcolor: (theme) => theme.palette.primary.main + "10",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              شماره پیگیری درخواست
            </Typography>
            <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ letterSpacing: 1 }}>
              {summary.referenceId || summary.ReferenceId || "—"}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">کد لید: {summary.leadCode || summary.LeadCode || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                موبایل: {summary.maskedMobile || summary.MaskedMobile || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                استان: {summary.provinceName || summary.ProvinceName || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">شهر: {summary.cityName || summary.CityName || "—"}</Typography>
            </Grid>
            {(summary.publicStatusNote || summary.PublicStatusNote) && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {summary.publicStatusNote || summary.PublicStatusNote}
                </Typography>
              </Grid>
            )}
          </Grid>
        </StepFormCard>
      ) : null}

      {!locked && activeStep === 0 ? (
        <StepFormCard
          maxWidth={FORM_NARROW_MAX}
          title="شماره موبایل متقاضی"
          description="شماره موبایل متقاضی را وارد کنید. کد تأیید برای همین شماره پیامک می‌شود."
        >
          <TextField
            fullWidth
            label="شماره موبایل"
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 11))}
            inputProps={{ inputMode: "tel", maxLength: 11 }}
            placeholder="09123456789"
            disabled={actionLoading}
          />
          <FormActions>
            <ActionButton
              variant="contained"
              onClick={handleStart}
              disabled={mobile.length < 11}
              loading={actionLoading}
            >
              ارسال کد تأیید
            </ActionButton>
          </FormActions>
        </StepFormCard>
      ) : null}

      {!locked && activeStep === 1 ? (
        <StepFormCard
          maxWidth={FORM_NARROW_MAX}
          title="تأیید پیامک"
          description={`کد ۵ رقمی ارسال‌شده به شماره ${mobile || "متقاضی"} را وارد کنید.`}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 1,
              direction: "ltr",
            }}
          >
            <OtpCodeInput
              type="number"
              fields={5}
              name="assistedRegistrationOtp"
              value={otp}
              onChange={setOtp}
              disabled={actionLoading}
              inputMode="numeric"
              pattern="[0-9]*"
              style={{
                direction: "ltr",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                gap: "8px",
              }}
              inputStyle={{
                width: "44px",
                height: "48px",
                fontSize: "1.25rem",
                textAlign: "center",
                direction: "ltr",
                unicodeBidi: "isolate",
                borderRadius: "8px",
                border: "1px solid #CBD5E1",
                outline: "none",
                margin: 0,
                MozAppearance: "textfield",
                WebkitAppearance: "none",
              }}
            />
          </Box>
          <FormActions>
            <ActionButton
              variant="outlined"
              onClick={handleBackToMobile}
              disabled={actionLoading}
            >
              تغییر شماره
            </ActionButton>
            <ActionButton
              variant="contained"
              onClick={handleVerify}
              disabled={otp.length < 5}
              loading={actionLoading}
            >
              تأیید کد
            </ActionButton>
          </FormActions>
        </StepFormCard>
      ) : null}

      {!locked && activeStep === 2 ? (
        <StepFormCard maxWidth={FORM_WIDE_MAX} title="پروفایل متقاضی">
          <ValidationErrorsAlert messages={validationMessages} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="نام"
                value={profile.firstName}
                onChange={(e) => {
                  setProfile((p) => ({ ...p, firstName: e.target.value }));
                  if (profileErrors.firstName) setProfileErrors((e) => ({ ...e, firstName: undefined }));
                }}
                error={Boolean(profileErrors.firstName)}
                helperText={profileErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="نام خانوادگی"
                value={profile.lastName}
                onChange={(e) => {
                  setProfile((p) => ({ ...p, lastName: e.target.value }));
                  if (profileErrors.lastName) setProfileErrors((e) => ({ ...e, lastName: undefined }));
                }}
                error={Boolean(profileErrors.lastName)}
                helperText={profileErrors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="کد ملی"
                value={profile.nationalCode}
                onChange={(e) => {
                  setProfile((p) => ({
                    ...p,
                    nationalCode: e.target.value.replace(/\D/g, "").slice(0, 10),
                  }));
                  if (profileErrors.nationalCode) setProfileErrors((er) => ({ ...er, nationalCode: undefined }));
                }}
                inputProps={{ maxLength: 10 }}
                error={Boolean(profileErrors.nationalCode)}
                helperText={profileErrors.nationalCode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="نام پدر"
                value={profile.fatherName}
                onChange={(e) => {
                  setProfile((p) => ({ ...p, fatherName: e.target.value }));
                  if (profileErrors.fatherName) setProfileErrors((er) => ({ ...er, fatherName: undefined }));
                }}
                error={Boolean(profileErrors.fatherName)}
                helperText={profileErrors.fatherName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <JalaliDatePicker
                label="تاریخ تولد"
                value={profile.birthDate}
                onChange={(date) => {
                  setProfile((p) => ({ ...p, birthDate: date }));
                  if (profileErrors.birthDate)
                    setProfileErrors((er) => ({ ...er, birthDate: undefined }));
                }}
                error={Boolean(profileErrors.birthDate)}
                helperText={profileErrors.birthDate || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="تلفن ثابت"
                value={profile.telephon}
                onChange={(e) => {
                  setProfile((p) => ({ ...p, telephon: e.target.value }));
                  if (profileErrors.telephon) setProfileErrors((er) => ({ ...er, telephon: undefined }));
                }}
                error={Boolean(profileErrors.telephon)}
                helperText={profileErrors.telephon}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ایمیل"
                value={profile.email}
                onChange={(e) => {
                  setProfile((p) => ({ ...p, email: e.target.value }));
                  if (profileErrors.email) setProfileErrors((er) => ({ ...er, email: undefined }));
                }}
                error={Boolean(profileErrors.email)}
                helperText={profileErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <FormActions>
                <ActionButton variant="contained" onClick={handleSaveProfile} loading={actionLoading}>
                  ادامه
                </ActionButton>
              </FormActions>
            </Grid>
          </Grid>
        </StepFormCard>
      ) : null}

      {!locked && activeStep >= 3 && activeStep <= 7 ? (
        <StepFormCard maxWidth={FORM_WIDE_MAX} title={STEPS[activeStep]}>
          <ValidationErrorsAlert messages={validationMessages} />
          <Formik
            innerRef={formikRef}
            initialValues={requestForm}
            enableReinitialize
            onSubmit={handleRequestStepNext}
          >
            {({
              values,
              errors,
              touched,
              handleSubmit: formikSubmit,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <form onSubmit={formikSubmit}>
                <Grid container spacing={2}>
                  <AssistedRegistrationRequestSteps
                    requestStep={requestStep}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    provinces={provinces}
                    cities={cities}
                    plantCapacityOptions={plantCapacityOptions}
                    baseUrl={baseUrl}
                    onParseLocation={handleParseLocation}
                    profileSummary={profileSummary}
                  />
                  <Grid item xs={12}>
                    <FormActions>
                      {activeStep > 3 ? (
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep((s) => s - 1)}
                          disabled={isSubmitting}
                        >
                          مرحله قبل
                        </Button>
                      ) : null}
                      <ActionButton
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting}
                        loading={actionLoading || isSubmitting}
                      >
                        {requestStep === 4
                          ? "ارسال برای بررسی سولارنت"
                          : requestStep === 3
                            ? "ذخیره و ادامه"
                            : "مرحله بعد"}
                      </ActionButton>
                    </FormActions>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </StepFormCard>
      ) : null}
    </PageShell>
  );
};

export default AssistedRegistrationPage;
